import React from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { FaInstagram, FaGlobe, FaWhatsapp, FaHandshake, FaUserTie } from "react-icons/fa";

/**
 * Componente ContactSection - Seção de contatos para rodapé
 *
 * Props:
 * @param {Object} contacts - Objeto com informações de contato
 * @param {string} className - Classes CSS adicionais
 */
const ContactSection = ({ contacts, className = "" }) => {
  const { t } = useTranslation();
  const currentYear = new Date().getFullYear();

  return (
    <footer
      className={`w-full bg-gray-800 text-white py-6 sm:py-10 ${className}`}
      id="contato"
    >
      <div className="w-full px-4 md:px-6 mx-auto max-w-7xl text-center">
        <h3 className="text-xl sm:text-2xl font-semibold mb-4 sm:mb-6">
          {t("home.contactTitle")}
        </h3>

        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-3 lg:grid-cols-6 gap-4 sm:gap-6 mb-8">
          {contacts.website && (
            <div className="flex flex-col items-center">
              <div className="w-10 h-10 sm:w-12 sm:h-12 bg-indigo-600 rounded-full flex items-center justify-center mb-2">
                <FaGlobe className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
              </div>
              <span className="text-xs sm:text-sm text-gray-300 mb-1">
                {t("home.contactWebsite")}
              </span>
              <a
                href={contacts.website}
                target="_blank"
                rel="noopener noreferrer"
                className="text-xs sm:text-sm hover:text-blue-400 transition-colors duration-200 text-center break-all"
              >
                {contacts.website.replace("https://", "")}
              </a>
            </div>
          )}

          {contacts.email && (
            <div className="flex flex-col items-center">
              <div className="w-10 h-10 sm:w-12 sm:h-12 bg-cyan-600 rounded-full flex items-center justify-center mb-2">
                <svg
                  className="w-5 h-5 sm:w-6 sm:h-6"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                  />
                </svg>
              </div>
              <span className="text-xs sm:text-sm text-gray-300 mb-1">
                {t("home.contactEmail")}
              </span>
              <a
                href={`mailto:${contacts.email}`}
                className="text-xs sm:text-sm hover:text-blue-400 transition-colors duration-200 text-center break-all"
              >
                {contacts.email}
              </a>
            </div>
          )}

          {contacts.instagram && (
            <div className="flex flex-col items-center">
              <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full flex items-center justify-center mb-2 bg-gradient-to-tr from-yellow-400 via-pink-500 to-purple-600">
                <FaInstagram className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
              </div>
              <span className="text-xs sm:text-sm text-gray-300 mb-1">
                {t("home.contactInstagram")}
              </span>
              <a
                href={`https://instagram.com/${contacts.instagram.replace(
                  "@",
                  ""
                )}`}
                target="_blank"
                rel="noopener noreferrer"
                className="text-xs sm:text-sm hover:text-blue-400 transition-colors duration-200"
              >
                {contacts.instagram}
              </a>
            </div>
          )}

          {contacts.phone && (
            <div className="flex flex-col items-center">
              <div className="w-10 h-10 sm:w-12 sm:h-12 bg-green-500 rounded-full flex items-center justify-center mb-2">
                <FaWhatsapp className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
              </div>
              <span className="text-xs sm:text-sm text-gray-300 mb-1">
                {t("home.contactPhone")}
              </span>
              <a
                href={`tel:${contacts.phone}`}
                className="text-xs sm:text-sm hover:text-blue-400 transition-colors duration-200"
              >
                {contacts.phone}
              </a>
            </div>
          )}

          {/* Affiliates Area */}
          <div className="flex flex-col items-center col-span-2 sm:col-span-1">
            <div className="w-10 h-10 sm:w-12 sm:h-12 bg-amber-600 rounded-full flex items-center justify-center mb-2">
              <FaHandshake className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
            </div>
            <span className="text-xs sm:text-sm text-gray-300 mb-1">
              {t("affiliates.area")}
            </span>
            <div className="flex flex-col items-center gap-1">
              <Link
                to="/affiliates/register"
                className="text-xs sm:text-sm hover:text-blue-400 transition-colors duration-200"
              >
                {t("common.register")}
              </Link>
              <Link
                to="/affiliates/login"
                className="text-xs sm:text-sm hover:text-blue-400 transition-colors duration-200"
              >
                {t("common.login")}
              </Link>
            </div>
          </div>

          {/* Employees Area */}
          <div className="flex flex-col items-center col-span-2 sm:col-span-1">
            <div className="w-10 h-10 sm:w-12 sm:h-12 bg-blue-600 rounded-full flex items-center justify-center mb-2">
              <FaUserTie className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
            </div>
            <span className="text-xs sm:text-sm text-gray-300 mb-1">
              {t("home.employeesArea")}
            </span>
            <div className="flex flex-col items-center gap-1">
              <Link
                to="/login"
                className="text-xs sm:text-sm hover:text-blue-400 transition-colors duration-200"
              >
                {t("common.login")}
              </Link>
            </div>
          </div>
        </div>

        <div className="border-t border-gray-700 pt-6">
          <p className="text-gray-400">
            {t("home.copyright").replace("2024", currentYear.toString())}
          </p>
          <p className="text-gray-400 mt-2">
            <Link
              to="/terms-of-use"
              className="hover:text-blue-400 transition-colors duration-200 underline"
            >
              {t("common.termsOfUse")}
            </Link>
          </p>
        </div>
      </div>
    </footer>
  );
};

export default ContactSection;
